/**
 * @file SpeechProcessor.ts
 *
 * Defines the `SpeechProcessor` public interface and the `BaseSpeechProcessor` abstract
 * class that all text-to-speech processors in the audio pipeline extend.
 *
 * Responsibilities:
 * - Owns the Azure `SpeechSynthesizer` instance, configured with
 *   `SpeechConfig.fromAuthorizationToken()` and `Audio24Khz48KBitRateMonoMp3` output
 *   format.
 * - Suppresses Azure's default device playback by routing synthesizer output through a
 *   dummy `PullAudioOutputStream` / `AudioConfig.fromStreamOutput()`. The stream is never
 *   consumed; it exists solely to satisfy the SDK's mandatory `AudioConfig` parameter.
 * - Conditionally creates an `AudioPlayer` for real audio output. When
 *   `outputConfig.textOnly` is `true` the `audioPlayer` field is `null` and no audio is
 *   ever submitted to the device.
 * - Warms up the Azure connection on construction by calling `speakTextAsync("Testing...")`
 *   so that the first real utterance does not pay the cold-start latency penalty.
 * - Maintains a `pendingText2SpeechQueue` (FIFO) and a `pollingInterval` whose concrete
 *   polling logic is implemented by each subclass.
 * - Exposes `setVoiceName(voiceName)` to swap the active TTS voice at runtime by resolving
 *   the name against `outputConfig.language.voices` and updating `outputConfig.voiceIndex`.
 * - Exposes `setSpeechToken(newToken)` to refresh the synthesizer's authorization token
 *   in-place without recreating the synthesizer.
 * - Provides `buildSsml()` to produce SSML with a `<prosody rate="...">` wrapper,
 *   using the caller-supplied language key, voice name, and pace value.
 * - Exposes `setOutputPace(pace)` and `getAudioPlayer()` as part of the `SpeechProcessor`
 *   interface.
 *
 * Pipeline position:
 *   SessionContext / HybridModelSessionContext
 *     → Text2SpeechProcessor | HybridModelText2SpeechProcessor (concrete subclasses)
 *       → BaseSpeechProcessor.speechSynthesizer  (Azure SDK)
 *         → AudioPlayer  (Web Audio API playback)
 */
import {AudioPlayer} from "./AudioPlayer";
import {Output} from "../contexts/Configuration";
import {
    AudioConfig,
    AudioOutputStream,
    SpeechConfig,
    SpeechSynthesisOutputFormat,
    SpeechSynthesizer
} from "microsoft-cognitiveservices-speech-sdk";
import {Queue} from "../contexts/Queue";

export interface SpeechProcessor {
    start(): void
    stop(): void
    getAudioPlayer(): AudioPlayer | null
    setOutputPace(pace: number): void
}

export abstract class BaseSpeechProcessor implements SpeechProcessor {
    //Services
    protected apiKey: string;
    protected region = "eastus";
    protected speechSynthesizer: SpeechSynthesizer;
    //Config
    protected outputConfig: Output;
    private readonly speechConfig: SpeechConfig;
    //Audio Player
    protected audioPlayer: AudioPlayer | null = null;
    //Pollers
    protected pollingInterval: NodeJS.Timer | undefined = undefined;

    protected currentRecognizedTokens: string[] | null = null;
    protected pendingText2SpeechQueue: Queue<string>;

    constructor(outputConfig: Output, azureSpeechAPIToken: string) {
        this.outputConfig = {...outputConfig};
        this.pendingText2SpeechQueue = new Queue<string>();

        this.apiKey = azureSpeechAPIToken;

        console.log('[Text2SpeechProcessor] Creating T2S processor...');
        this.speechConfig = SpeechConfig.fromAuthorizationToken(this.apiKey, this.region);
        /*this.speechConfig.speechSynthesisLanguage = outputConfig.language.speakKey;
        this.speechConfig.speechSynthesisVoiceName = outputConfig.language.voices[0];*/
        this.speechConfig.speechSynthesisOutputFormat = SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3

        this.audioPlayer = outputConfig.textOnly ? null : new AudioPlayer(outputConfig.device.deviceId, outputConfig.gain ?? 0.5);

        //Not sure why; I think this disables default Azure behavior of playing back sound automatically on default device
        //We wont use this stream nor the AudioConfig either, but it is mandatory for creating a Synthesizer
        const pullAudioOutputStream = AudioOutputStream.createPullStream();
        const audioConfig = AudioConfig.fromStreamOutput(pullAudioOutputStream);

        this.speechSynthesizer = new SpeechSynthesizer(this.speechConfig, audioConfig);
        this.warmUpConnection();

        this.currentRecognizedTokens = [];

        console.log('[Text2SpeechProcessor] Ready and connected to Services and Devices');
    }

    abstract start(): void;

    public stop(): void {
        clearInterval(this.pollingInterval);
        this.pollingInterval = undefined;
        this.speechSynthesizer.close();
        this.audioPlayer?.stop();
    }

    public setOutputPace(pace: number) {
        this.outputConfig.pace = pace;
    }

    public setVoiceName(voiceName: string): void {
        console.log(this.outputConfig.language.voices, voiceName)
        const idx = this.outputConfig.language.voices.findIndex(v => v.name === voiceName);
        console.log(this.outputConfig.language.voices, voiceName, idx)
        if (idx >= 0) {
            this.outputConfig.voiceIndex = idx;
        }
    }

    public setSpeechToken(newToken: string) {
        this.speechSynthesizer.authorizationToken = newToken;
    }

    public setOutputDevice(device: MediaDeviceInfo | null, gain?: number, force: boolean = false): void {
        if (this.outputConfig.textOnly || !device?.deviceId) {
            return;
        }

        this.outputConfig.device = device;
        if (gain !== undefined) {
            this.outputConfig.gain = gain;
            this.audioPlayer?.setVolume(gain);
        }

        this.audioPlayer?.setOutputDevice(device.deviceId, force);
        if (gain !== undefined) {
            this.audioPlayer?.setVolume(gain);
        }
    }

    public getAudioPlayer(): AudioPlayer | null {
        return this.audioPlayer;
    }

    /**
     * Rebuilds the playback AudioContext so its playout stream registers with the
     * echo canceller of a newly (re)acquired microphone capture (see
     * AudioPlayer.resetOutputContext). No-op for text-only outputs.
     */
    public resetAudioOutput(): void {
        this.audioPlayer?.resetOutputContext();
    }

    protected warmUpConnection() {
        console.log('[SpeechProcessor] Warming up connection with dummy text...');
        this.speechSynthesizer.speakTextAsync("Testing...");
    }

    protected buildSsml(textToSpeak: string,
                      lang: string,
                      voice: string,
                      pace: number) {
        return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${lang}"><voice name="${voice}"><prosody rate="${pace}">${textToSpeak}</prosody></voice></speak>`;
        //console.log('Build SSML: ', ssml);
    }
}